import React, { useEffect } from "react";
import validator from 'validator';
import ImageUploader from "../imageUploader";
import { useTranslation } from "react-i18next";

const languageOptions = [
    { value: 'english', labelKey: 'language-english' },
    { value: 'spanish', labelKey: 'language-spanish' },
    { value: 'french', labelKey: 'language-french' },
    { value: 'german', labelKey: 'language-german' },
    { value: 'italian', labelKey: 'language-italian' },
    { value: 'portuguese', labelKey: 'language-portuguese' },
    { value: 'chinese', labelKey: 'language-chinese' },
    { value: 'russian', labelKey: 'language-russian' },
    { value: 'korean', labelKey: 'language-korean' },
    { value: 'japanese', labelKey: 'language-japanese' },
    { value: 'arabic', labelKey: 'language-arabic' },
    { value: 'hebrew', labelKey: 'language-hebrew' },
]

const pinColorOptions = [
    '#E53935', '#D81B60', '#8E24AA', '#5E35B1',
    '#3949AB', '#1E88E5', '#00897B', '#43A047',
    '#F9A825', '#FB8C00', '#6D4C41', '#546E7A',
];

const pinEmojiOptions = [
    '📍', '🎧', '🎙️', '⭐️', '👻', '🏛️',
    '🍴', '🌲', '🎨', '📚', '🎭', '🧭',
];

const normalizePinEmoji = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '📍';

    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
        const firstSegment = Array.from(segmenter.segment(trimmed))[0];
        return firstSegment?.segment || '📍';
    }

    return Array.from(trimmed)[0] || '📍';
};

const TourGuideForm = ({ me, locations, formSubmitFunc, tourguide, imageUploadFunc }) => {
    const { t } = useTranslation();
    const [name, setName] = React.useState("");
    const [nameError, setNameError] = React.useState(null);
    const [avatarUrl, setAvatarUrl] = React.useState("");
    const [bio, setBio] = React.useState("");
    const [bioError, setBioError] = React.useState(null);
    const [location, setLocation] = React.useState("");
    const [locationError, setLocationError] = React.useState(null);
    const [languages, setLanguages] = React.useState([]);
    const [languagesError, setLanguagesError] = React.useState(null);
    const [website, setWebsite] = React.useState("");
    const [websiteError, setWebsiteError] = React.useState(null);
    const [facebook, setFacebook] = React.useState("");
    const [facebookError, setFacebookError] = React.useState(null);
    const [instagram, setInstagram] = React.useState("");
    const [instagramError, setInstagramError] = React.useState(null);
    const [twitter, setTwitter] = React.useState("");
    const [twitterError, setTwitterError] = React.useState(null);
    const [linkedin, setLinkedin] = React.useState("");
    const [linkedinError, setLinkedinError] = React.useState(null);
    const [tiktok, setTiktok] = React.useState("");
    const [tiktokError, setTiktokError] = React.useState(null);
    const [pinColor, setPinColor] = React.useState("#FF5733");
    const [pinEmoji, setPinEmoji] = React.useState("📍");

    useEffect(() => {
        if (tourguide) {
            setName(tourguide.name || "")
            setBio(tourguide.bio || "")
            setLocation(tourguide.location_id || "")
            setFacebook(tourguide.facebook || "")
            setInstagram(tourguide.instagram || "")
            setTwitter(tourguide.twitter || "")
            setLinkedin(tourguide.linkedin || "")
            setTiktok(tourguide.tiktok || "")
            setAvatarUrl(tourguide.avatar_url || "")
            setWebsite(tourguide.webpage || "")
            setLanguages((tourguide.languages && tourguide.languages.split(",")) || [])
            setPinColor(tourguide.pin_color || "#FF5733")
            setPinEmoji(normalizePinEmoji(tourguide.pin_emoji || "📍"))
        }
    }, [tourguide])

    const validateAndSubmit = (e) => {
        e.preventDefault();

        // Clear all errors
        setNameError(null)
        setBioError(null)
        setLocationError(null)
        setFacebookError(null)
        setInstagramError(null)
        setTwitterError(null)
        setLinkedinError(null)
        setTiktokError(null)
        setWebsiteError(null)
        setLanguagesError(null)

        let errors = false;

        if (name === "") {
            setNameError(t('guide-name-required'))
            errors = true;
        }

        if (bio === "") {
            setBioError(t('guide-bio-required'))
            errors = true;
        }

        if (location === "") {
            setLocationError(t('guide-location-required'))
            errors = true;
        }

        if (languages.length < 1) {
            setLanguagesError(t('guide-language-required'))
            errors = true;
        }

        if (website && !validator.isURL(website)) {
            setWebsiteError(t('valid-url-error'))
            errors = true;
        }

        if (facebook && !validator.isURL(facebook)) {
            setFacebookError(t('valid-url-error'))
            errors = true;
        }

        if (instagram && !validator.isURL(instagram)) {
            setInstagramError(t('valid-url-error'))
            errors = true;
        }

        if (tiktok && !validator.isURL(tiktok)) {
            setTiktokError(t('valid-url-error'))
            errors = true;
        }

        if (linkedin && !validator.isURL(linkedin)) {
            setLinkedinError(t('valid-url-error'))
            errors = true;
        }

        if (twitter && !validator.isURL(twitter)) {
            setTwitterError(t('valid-url-error'))
            errors = true;
        }

        if (!twitter && !facebook && !instagram && !website && !linkedin && !tiktok) {
            setWebsiteError(t('guide-contact-required'))
            errors = true;
        }

        if (errors) return;

        formSubmitFunc({
            name,
            avatar_url: avatarUrl,
            languages: languages.join(","),
            user_id: me.id,
            email: me.email,
            location_id: location,
            bio,
            webpage: website,
            facebook,
            instagram,
            twitter,
            linkedin,
            tiktok,
            pin_color: pinColor,
            pin_emoji: normalizePinEmoji(pinEmoji),
        })
    }

    const handleLanguageChange = (language, checked) => {
        if (checked) {
            if (!languages.includes(language)) {
                setLanguages([...languages, language]);
            }
        } else {
            setLanguages(languages.filter((l) => l !== language));
        }
    };

    return (
        <form onSubmit={validateAndSubmit} className="tour-form">
            <div className="form-section">
                <label className="form-section-title">{t('guide-profile-photo')}</label>
                <ImageUploader imageUrls={[avatarUrl]} imageUploadFunc={imageUploadFunc} />
            </div>

            <div className="form-section pin-style-section">
                <label className="form-section-title">{t('guide-map-pin')}</label>
                <div className="pin-style-preview-row">
                    <div className="pin-style-preview" style={{ backgroundColor: pinColor }}>
                        <span>{normalizePinEmoji(pinEmoji)}</span>
                        <span className="pin-style-category-badge" style={{ color: pinColor }}>
                            <i className="bi bi-moon-stars-fill"></i>
                        </span>
                    </div>
                </div>

                <label className="form-label">{t('guide-color')}</label>
                <div className="pin-color-grid">
                    {pinColorOptions.map((color) => (
                        <button
                            key={color}
                            type="button"
                            className={`pin-color-option${pinColor === color ? ' selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setPinColor(color)}
                            aria-label={t('guide-pin-color-aria', { color })}
                        >
                            {pinColor === color && <i className="bi bi-check-lg"></i>}
                        </button>
                    ))}
                </div>

                <label htmlFor="pinEmoji" className="form-label">{t('guide-emoji')}</label>
                <div className="pin-emoji-row">
                    <input
                        id="pinEmoji"
                        className="form-input pin-emoji-input"
                        value={pinEmoji}
                        onChange={(e) => setPinEmoji(e.target.value)}
                        onBlur={() => setPinEmoji(normalizePinEmoji(pinEmoji))}
                    />
                    <div className="pin-emoji-grid">
                        {pinEmojiOptions.map((emoji) => (
                            <button
                                key={emoji}
                                type="button"
                                className={`pin-emoji-option${normalizePinEmoji(pinEmoji) === normalizePinEmoji(emoji) ? ' selected' : ''}`}
                                onClick={() => setPinEmoji(normalizePinEmoji(emoji))}
                                aria-label={t('guide-pin-emoji-aria', { emoji })}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="form-section">
                <label htmlFor="name" className="form-label">{t('guide-name')}</label>
                <input
                    id="name"
                    className={`form-input ${nameError ? 'error' : ''}`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('guide-name-placeholder')}
                />
                <small className="text-muted">{t('guide-name-help')}</small>
                {nameError && <span className="error-text">{nameError}</span>}
            </div>

            <div className="form-section">
                <label htmlFor="selectLocation" className="form-label">{t('guide-location')}</label>
                <select
                    id="selectLocation"
                    className={`form-input ${locationError ? 'error' : ''}`}
                    value={location || ''}
                    onChange={(e) => setLocation(e.target.value)}
                >
                    <option value="" disabled>{t('guide-location-placeholder')}</option>
                    {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                            {loc.flag} {loc.city}, {loc.country}
                        </option>
                    ))}
                </select>
                <small className="text-muted">
                    {t('guide-location-missing')} <a href="mailto:admin@castlerock.ai">{t('guide-contact-us')}</a>
                </small>
                {locationError && <span className="error-text">{locationError}</span>}
            </div>

            <div className="form-section">
                <label className="form-label">{t('guide-languages-spoken')}</label>
                <div className={`checkbox-grid ${languagesError ? 'error' : ''}`}>
                    {languageOptions.map((language, index) => (
                        <div key={index} className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id={`language-${language.value}`}
                                value={language.value}
                                onChange={(e) => handleLanguageChange(language.value, e.target.checked)}
                                checked={languages.includes(language.value)}
                            />
                            <label className="form-check-label" htmlFor={`language-${language.value}`}>
                                {t(language.labelKey)}
                            </label>
                        </div>
                    ))}
                </div>
                {languagesError && <span className="error-text">{languagesError}</span>}
            </div>

            <div className="form-section">
                <label htmlFor="bio" className="form-label">{t('guide-bio')}</label>
                <textarea
                    id="bio"
                    rows="4"
                    className={`form-input textarea ${bioError ? 'error' : ''}`}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder={t('guide-bio-placeholder')}
                />
                {bioError && <span className="error-text">{bioError}</span>}
            </div>

            <hr className="my-4" />

            <h3 className="mb-3">{t('guide-contact-info')}</h3>
            <p className="text-muted mb-4">{t('guide-contact-info-help')}</p>

            <div className="form-section">
                <label htmlFor="selectWebpage" className="form-label">
                    <i className="bi bi-globe me-2"></i>{t('guide-website')}
                </label>
                <input
                    id="selectWebpage"
                    className={`form-input ${websiteError ? 'error' : ''}`}
                    placeholder="https://yourwebsite.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                />
                {websiteError && <span className="error-text">{websiteError}</span>}
            </div>

            <div className="form-row">
                <div className="form-section half">
                    <label htmlFor="selectFacebook" className="form-label">
                        <i className="bi bi-facebook me-2"></i>Facebook
                    </label>
                    <input
                        id="selectFacebook"
                        className={`form-input ${facebookError ? 'error' : ''}`}
                        value={facebook}
                        placeholder="https://facebook.com/yourprofile"
                        onChange={(e) => setFacebook(e.target.value)}
                    />
                    {facebookError && <span className="error-text">{facebookError}</span>}
                </div>

                <div className="form-section half">
                    <label htmlFor="selectInstagram" className="form-label">
                        <i className="bi bi-instagram me-2"></i>Instagram
                    </label>
                    <input
                        id="selectInstagram"
                        className={`form-input ${instagramError ? 'error' : ''}`}
                        value={instagram}
                        placeholder="https://instagram.com/yourprofile"
                        onChange={(e) => setInstagram(e.target.value)}
                    />
                    {instagramError && <span className="error-text">{instagramError}</span>}
                </div>
            </div>

            <div className="form-row">
                <div className="form-section half">
                    <label htmlFor="selectTwitter" className="form-label">
                        <i className="bi bi-twitter-x me-2"></i>X / Twitter
                    </label>
                    <input
                        id="selectTwitter"
                        className={`form-input ${twitterError ? 'error' : ''}`}
                        placeholder="https://x.com/yourprofile"
                        value={twitter}
                        onChange={(e) => setTwitter(e.target.value)}
                    />
                    {twitterError && <span className="error-text">{twitterError}</span>}
                </div>

                <div className="form-section half">
                    <label htmlFor="selectLinkedin" className="form-label">
                        <i className="bi bi-linkedin me-2"></i>LinkedIn
                    </label>
                    <input
                        id="selectLinkedin"
                        className={`form-input ${linkedinError ? 'error' : ''}`}
                        placeholder="https://linkedin.com/in/yourprofile"
                        value={linkedin}
                        onChange={(e) => setLinkedin(e.target.value)}
                    />
                    {linkedinError && <span className="error-text">{linkedinError}</span>}
                </div>
            </div>

            <div className="form-section">
                <label htmlFor="tiktok" className="form-label">
                    <i className="bi bi-tiktok me-2"></i>TikTok
                </label>
                <input
                    id="tiktok"
                    className={`form-input ${tiktokError ? 'error' : ''}`}
                    value={tiktok}
                    placeholder="https://tiktok.com/@yourprofile"
                    onChange={(e) => setTiktok(e.target.value)}
                />
                {tiktokError && <span className="error-text">{tiktokError}</span>}
            </div>

            <div className="form-actions">
                <button type="submit" className="btn-primary">{t('guide-save-profile')}</button>
            </div>
        </form>
    )
}

export default TourGuideForm;
